import { mapObjectProperties } from '~/_shared/utils/collections/collections';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { isDataLayersArtificialBoundaryTerritoryGroup } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.helpers';
import {
  checkIfBoundaryGroupIsMOTGenerated, checkIfBoundaryGroupIsWMSGenerated,
} from '~/store/boundaryGroups/boundaryGroups.helpers';
import {
  boundaryGroupsSelector, findBoundaryGroup,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { isDataLayerToolActiveSelector } from '~/store/frontendState/mapTools/dataLayers/dataLayers.selectors';
import {
  mapSettingsBoundariesPrimaryBoundaryGroupIdSelector, mapSettingsBoundaryOrderSelector,
} from '~/store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import { mapSettingsDataLayersPrimaryBoundaryGroupIdSelector } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import { activeBTGIdPerBoundaryGroupSelector } from '~/store/mapSettings/toolsState/boundary/activeBTG.selectors';
import { isMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import {
  optimizedTerritoriesEnabledSelector, optimizedWMSTerritoriesEnabledSelector,
} from '~/store/userData/userData.selectors';
import { type AppState } from '../app.store';
import type {
  BoundaryGroupId, BoundaryTerritoryGroupId,
} from '../boundaries/boundaryIdentifier.type';

export const boundaryTerritoryGroupsSelector = (state: AppState): ReadonlyArray<BoundaryTerritoryGroup> => state.map.boundaryTerritoryGroups.groups;
export const useBoundaryTerritoryGroupsSelector = () => useSelector(boundaryTerritoryGroupsSelector);

export const boundaryTerritoryGroupsIsLoadingSelector = (state: AppState) =>
  state.map.boundaryTerritoryGroups.isFetchLoading || state.map.boundaryTerritoryGroups.isActionLoading;
export const useBoundaryTerritoryGroupsIsLoadingSelector = () => useSelector(boundaryTerritoryGroupsIsLoadingSelector);

export const boundaryTerritoryGroupsLoadedSelector = (state: AppState) => state.map.boundaryTerritoryGroups.isLoaded;

const findBoundaryTerritoryGroup = (boundaryTerritoryGroups: readonly BoundaryTerritoryGroup[], id?: BoundaryTerritoryGroupId | null) =>
  boundaryTerritoryGroups.find(btg => btg.boundaryTerritoryGroupId === id);

export const findBoundaryTerritoryGroupSelector = createStateSelector([
  boundaryTerritoryGroupsSelector,
], (boundaryTerritoryGroups) => {
  return (id?: BoundaryTerritoryGroupId | null) => {
    return findBoundaryTerritoryGroup(boundaryTerritoryGroups, id);
  };
});

export type GetBTGById = ReturnType<typeof findBoundaryTerritoryGroupSelector>;

export const useGetBoundaryTerritoryGroup = () => {
  const getBoundaryTerritoryGroupById = useSelector(findBoundaryTerritoryGroupSelector);

  return {
    getBoundaryTerritoryGroupById,
  };
};

export type BoundaryGroupInfoWithBTGs = Readonly<{
  boundaryGroupId: number;
  boundaryTerritoryGroups: [null, ...NonEmptyArray<BoundaryTerritoryGroup>] | NonEmptyArray<BoundaryTerritoryGroup>;
}>;

export const boundaryIsOptimizedTerritoriesTrialSelector = createStateSelector([
  boundaryGroupsSelector,
  optimizedTerritoriesEnabledSelector,
  optimizedWMSTerritoriesEnabledSelector,
],
(boundaryGroups, optimizedTerritoriesEnabled, optimizedWMSTerritoriesEnabled) => {
  return (boundaryGroupId: BoundaryGroupId) => {

    const boundaryGroup = findBoundaryGroup(boundaryGroups, boundaryGroupId);
    if (!boundaryGroup) {
      return false;
    }
    const isBoundaryAiWMSGenerated = checkIfBoundaryGroupIsWMSGenerated(boundaryGroup);
    const isBoundaryAiGenerated = checkIfBoundaryGroupIsMOTGenerated(boundaryGroup);

    const isBoundaryAiWMSTemporary = boundaryGroup.wms.wmsTemporary;
    const isBoundaryAiTemporary = boundaryGroup.mot.motTemporary;

    return (isBoundaryAiWMSGenerated && !optimizedWMSTerritoriesEnabled && isBoundaryAiWMSTemporary) ||
        (isBoundaryAiGenerated && !optimizedTerritoriesEnabled && isBoundaryAiTemporary);
  };
});

export const BTGsGroupedByBoundaryGroupSelector: ((state: AppState) => Readonly<{
  all: ReadonlyArray<BoundaryGroupInfoWithBTGs>;
  boundaryTool: ReadonlyArray<BoundaryGroupInfoWithBTGs>;
  dataLayersTool: ReadonlyArray<BoundaryGroupInfoWithBTGs>;
}>) = createStateSelector([
  boundaryTerritoryGroupsSelector,
  mapSettingsBoundaryOrderSelector,
  isMapEditDisabled,
  boundaryIsOptimizedTerritoriesTrialSelector,
], (
  boundaryTerritoryGroups,
  boundaryGroupOrder,
  isEditModeDisabled,
  checkIfBoundaryIsOptimizedTerritoriesTrial,
) => {
  const isHidden = (boundary: BoundaryTerritoryGroup) => {
    const boundaryIsOptimizedTerritoriesTrial = checkIfBoundaryIsOptimizedTerritoriesTrial(boundary.boundaryGroupId);
    return isEditModeDisabled && boundaryIsOptimizedTerritoriesTrial;
  };

  const groupedBTGsByBoundaryGroup = boundaryTerritoryGroups
    .filter(item => !isHidden(item))
    .sort((btgA, btgB) => {
      // This sorting ensures that the boundary groups with the lowest ID are at the top
      return btgA.boundaryTerritoryGroupId - btgB.boundaryTerritoryGroupId;
    })
    .reduce((acc, boundaryTerritoryGroup) => {
      const currentGroupInfo = acc[boundaryTerritoryGroup.boundaryGroupId];
      if (currentGroupInfo) {
        currentGroupInfo.boundaryTerritoryGroups = [
          ...currentGroupInfo.boundaryTerritoryGroups,
          boundaryTerritoryGroup,
        ];
      }
      else {
        acc[boundaryTerritoryGroup.boundaryGroupId] = {
          boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
          boundaryTerritoryGroups: [boundaryTerritoryGroup],
        };
      }

      return acc;
    }, {} as Record<number, Mutable<BoundaryGroupInfoWithBTGs>>);

  const orderedGroupedBTGs = mapObjectProperties((btgInfo) => {
    const manualFill = btgInfo.boundaryTerritoryGroups.find(btg => btg?.settings?.boundaryTerritoryType === BoundaryTerritoryType.Manual) ?? null;
    const otherFills = btgInfo.boundaryTerritoryGroups
      .filter(btg => btg !== manualFill)
      // Absolute value is used to correctly order artificial BTGs with negative IDs
      .sort((btgA, btgB) => Math.abs(btgA?.boundaryTerritoryGroupId ?? 0) - Math.abs(btgB?.boundaryTerritoryGroupId ?? 0));

    return {
      ...btgInfo,
      boundaryTerritoryGroups: [manualFill, ...otherFills],
    } as BoundaryGroupInfoWithBTGs;
  }, groupedBTGsByBoundaryGroup);

  const BTGsWithoutGroupOrder = Object.values(orderedGroupedBTGs)
    .map((btgInfo) => {
      if (boundaryGroupOrder.includes(btgInfo.boundaryGroupId)) {
        return null;
      }
      return btgInfo;
    })
    .filter(notNullsy);

  const all = [...BTGsWithoutGroupOrder, ...boundaryGroupOrder.map(id => orderedGroupedBTGs[id]).filter(notNullsy)];
  const boundaryTool = all.filter(groupInfo => groupInfo.boundaryTerritoryGroups.some(btg => !!btg && !isDataLayersArtificialBoundaryTerritoryGroup(btg)));
  const dataLayersTool = all.filter(groupInfo => groupInfo.boundaryTerritoryGroups.some(btg => !!btg && isDataLayersArtificialBoundaryTerritoryGroup(btg)));

  return {
    all,
    boundaryTool,
    dataLayersTool,
  };
});

export const useBTGsGroupedByBoundaryGroupSelector = () => useSelector(BTGsGroupedByBoundaryGroupSelector);

export const activeBoundaryTerritoryGroupsSelector = createStateSelector([
  BTGsGroupedByBoundaryGroupSelector,
  activeBTGIdPerBoundaryGroupSelector,
  (state, isDataLayerToolActive?: boolean) => isDataLayerToolActive ?? isDataLayerToolActiveSelector(state),
], (
  boundaryTerritoryGroupsGroupedByBoundary,
  activeBTGIdPerBoundaryGroup,
  isDataLayerToolActive,
): ReadonlyArray<BoundaryTerritoryGroup> => {
  return (
    isDataLayerToolActive
      ? boundaryTerritoryGroupsGroupedByBoundary.dataLayersTool
      : boundaryTerritoryGroupsGroupedByBoundary.boundaryTool
  ).map(groupInfo => {
    const { boundaryGroupId, boundaryTerritoryGroups } = groupInfo;
    const activeBTGId = activeBTGIdPerBoundaryGroup[boundaryGroupId] ??
      boundaryTerritoryGroups[1]?.boundaryTerritoryGroupId ??
      boundaryTerritoryGroups[0]?.boundaryTerritoryGroupId ?? 0;
    const activeBoundaryTerritoryGroup =
      findBoundaryTerritoryGroup(boundaryTerritoryGroups.filter(notNullsy), activeBTGId) ?? boundaryTerritoryGroups[1] ?? boundaryTerritoryGroups[0] as BoundaryTerritoryGroup;
    return activeBoundaryTerritoryGroup;
  });
});
export const useActiveBoundaryTerritoryGroupsSelector = () => useSelector(activeBoundaryTerritoryGroupsSelector);

export const primaryBoundaryGroupIdSelector = createStateSelector([
  mapSettingsBoundariesPrimaryBoundaryGroupIdSelector,
  mapSettingsDataLayersPrimaryBoundaryGroupIdSelector,
  isDataLayerToolActiveSelector,
], (
  mapSettingsBoundariesPrimaryBoundaryGroupId,
  mapSettingsDataLayersPrimaryBoundaryGroupId,
  isDataLayerToolActive,
) => {
  if (isDataLayerToolActive) {
    return mapSettingsDataLayersPrimaryBoundaryGroupId;
  }
  return mapSettingsBoundariesPrimaryBoundaryGroupId;
});
export const usePrimaryBoundaryGroupIdSelector = () => useSelector(primaryBoundaryGroupIdSelector);

const primaryBoundaryGroupBoundaryTerritoryGroupsSelector = createStateSelector([
  BTGsGroupedByBoundaryGroupSelector,
  primaryBoundaryGroupIdSelector,
  isDataLayerToolActiveSelector,
], (
  boundaryTerritoryGroupsGroupedByBoundaryGroup,
  primaryBoundaryGroupId,
  isDataLayerToolActive,
) => {
  if (primaryBoundaryGroupId) {
    return (
      isDataLayerToolActive
        ? boundaryTerritoryGroupsGroupedByBoundaryGroup.dataLayersTool
        : boundaryTerritoryGroupsGroupedByBoundaryGroup.boundaryTool
    )
      .find(groupInfo => groupInfo.boundaryGroupId === primaryBoundaryGroupId)
      ?.boundaryTerritoryGroups ?? [];
  }

  return [];
});

export const usePrimaryBoundaryGroupBoundaryTerritoryGroups = () => useSelector(
  primaryBoundaryGroupBoundaryTerritoryGroupsSelector,
);
